import DownloadService from "~/services/DownloadService";
import deploymentActions from "./deploymentActions";

export default {
  ...deploymentActions,

  async fetch({ dispatch, commit }, { agencyId } = {}) {
    commit("SET_LOADING", true);
    try {
      await Promise.all([
        dispatch("setVaccines", { agencyId }),
        dispatch("setOses"),
        dispatch("setTypes"),
        dispatch("setCategories"),
      ]);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async setVaccines({ state, commit, rootGetters }, payload = {}) {
    const { page, sortBy, orderBy } = state;
    const filter = payload.filter ?? "";
    const size = rootGetters["application/getPageSize"];
    const agencyId = payload.agencyId ?? rootGetters["application/getSelectedAgency"]?.agencyId;

    let vaccines;
    if (agencyId) {
      vaccines = await this.$services.vaccine.fetchByAgency(agencyId, {
        size,
        page,
        sortBy,
        orderBy,
        filter,
      });
    } else {
      vaccines = await this.$services.vaccine.fetch({ size, page, sortBy, orderBy, filter });
    }
    commit("SET_VACCINES", vaccines);
  },

  async setVaccinesDQL({ state, commit, rootGetters }, { filters, agencyId: payloadAgencyId }) {
    const { page, sortBy, orderBy } = state;
    const size = rootGetters["application/getPageSize"];
    const agencyId = payloadAgencyId ?? rootGetters["application/getSelectedAgency"]?.agencyId;

    let vaccines;
    if (agencyId) {
      vaccines = await this.$services.vaccine.searchDqlByAgency(
        agencyId,
        { size, page, sortBy, orderBy },
        filters,
      );
    } else {
      vaccines = await this.$services.vaccine.searchDql({ size, page, sortBy, orderBy }, filters);
    }
    commit("SET_VACCINES", vaccines);
  },

  async fetchVaccine({ commit }, id) {
    const editingVaccine = await this.$services.vaccine.fetchOne(id);
    commit("SET_EDITING_VACCINE", editingVaccine);
  },

  async addVaccine({ commit }, savedVaccine) {
    try {
      const createdVaccine = await this.$services.vaccine.create(savedVaccine);
      commit("ADD_VACCINE", createdVaccine);
      this.$feedback.ok("feedback.create.vaccine.ok");
    } catch (error) {
      this.$feedback.error("feedback.create.vaccine.error");
    }
  },

  async setVaccine({ commit }, { savedVaccine, id }) {
    try {
      const vaccine = await this.$services.vaccine.patch(id, savedVaccine);
      commit("SET_VACCINE", vaccine);
      this.$feedback.ok("feedback.update.vaccine.ok");
    } catch (error) {
      this.$feedback.error("feedback.update.vaccine.error");
    }
  },

  async deleteVaccine({ commit }, id) {
    try {
      await this.$services.vaccine.remove(id);
      commit("DELETE_VACCINE", id);
      this.$feedback.ok("feedback.delete.vaccine.ok");
    } catch (error) {
      this.$feedback.error("feedback.delete.vaccine.error");
    }
  },

  async setVaccineList({ commit }) {
    const vaccineList = await this.$services.vaccine.fetchAll();
    commit("SET_VACCINE_LIST", vaccineList);
  },

  async setOses({ state, commit, rootGetters }) {
    const isAdmin = rootGetters["application/isAdmin"];
    if (isAdmin && !state.allOses.length) {
      const oses = await this.$services.vaccine.fetchOses();
      commit("SET_OSES", oses);
    }
  },

  async setTypes({ commit, rootGetters, state }) {
    const isAdmin = rootGetters["application/isAdmin"];
    if (isAdmin && !state.allTypes.length) {
      const types = await this.$services.vaccine.fetchTypes();
      commit("SET_TYPES", types);
    }
  },

  async setCategories({ commit, rootGetters, state }) {
    const isAdmin = rootGetters["application/isAdmin"];
    if (isAdmin && !state.allCategories.length) {
      const categories = await this.$services.vaccine.fetchCategories();
      commit("SET_CATEGORIES", categories);
    }
  },

  async setFields({ commit }) {
    const fields = await this.$services.vaccine.fetchDqlFields();
    commit("SET_FIELDS", fields);
  },

  async exportVaccines() {
    const data = await this.$services.vaccine.exportZip();
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement("a");
    link.href = url;
    const title = this.$i18n.t("vaccines.label");
    link.setAttribute("download", `microclaudia - ${title.toLowerCase()}.zip`);
    document.body.appendChild(link);
    link.click();
  },

  async importVaccines({ dispatch }, { payload }) {
    const { file } = payload;
    const formData = new FormData();
    formData.append("file", file);

    try {
      await this.$services.vaccine.importZip(formData);
      dispatch("setVaccines");
      this.$feedback.ok("feedback.import.vaccine.ok");
    } catch (error) {
      this.$feedback.error("feedback.import.vaccine.error");
    }
  },

  async prepareCsv({ rootGetters, commit, dispatch }, requestParams) {
    const { agencyId } = rootGetters["application/getSelectedAgency"];
    const endpoint = agencyId
      ? `/agencies/${agencyId}/vaccines/preparecsv`
      : `/vaccines/preparecsv`;
    await DownloadService.prepareDownload(this.$axios, endpoint, requestParams, {
      dispatch,
      commit,
      $feedback: this.$feedback,
    });
  },

  async prepareZip({ commit, dispatch }, requestParams) {
    await DownloadService.prepareDownload(this.$axios, `/vaccines/preparezip`, requestParams, {
      dispatch,
      commit,
      $feedback: this.$feedback,
    });
  },

  setLoading({ commit }, loading) {
    commit("SET_LOADING", loading);
  },
};
